import { ROUTES } from "../components/shared/constant";
import { IRouteDetails } from "../common/App.interface";
import { LegacyOnboardingRedirect } from "../views/onboarding/LegacyOnboardingRedirect";
import {
  SkyAgentAdminRedirect,
  SkyAgentCapabilitiesRedirect,
  SkyAgentMemoryRedirect,
  SkyBrainSessionHistoryRedirect,
} from "../views/skyagent/pages/legacyRedirects";
import {
  HomeRoute,
  ReferralHubOverview,
  OpenRolesPage,
  MyReferralsPage,
  RewardsPage,
  Apps,
  Platform,
  Account,
  SurveysPage,
  SurveyBuilderPage,
  SurveyDetailPage,
  SurveyPreviewPage,
  SurveyTemplatesPage,
  PublicSurveyPage,
  TenantsSettings,
  InstanceProductsPage,
  PerformanceHome,
  PerformanceItHome,
  PerformanceSettingsPage,
  PerformanceSettingsHub,
  CoachingLogPage,
  CoachingRecordPage,
  PersonFilePage,
  TodosPage,
  PlansPage,
  PlanPage,
  MyPlanPage,
  EvaluationsPage,
  EvaluationPage,
  SelfEvaluationPage,
  VisitsPage,
  VisitPage,
  RecordsPage,
  DocumentsPage,
  OrgChartPage,
  CreateTenant,
  LogsPage,
  UsersPage,
  RoleManagementPage,
  ClientsManagementPage,
  ErrorPage,
  GeneralSettingsPage,
  SettingsHubPage,
  TemplatesHubPage,
  LocationsPage,
  LeadsPage,
  RediscoveryPage,
  DocumentCentrePage,
  ScheduledToursPage,
  CrmIntegrationsPage,
  OnboardDashboard,
  OfferTemplatesPage,
  SkyAgentPage,
  SkyAgentAIEmployeesPage,
  SkyAgentApprovalsPage,
  SkyAgentArtifactsPage,
  SkyAgentConfigurationPage,
  SkyBrainOverviewPage,
  SkyBrainMemoryPage,
  SkyBrainSkillsPage,
  SkyBrainQuickPromptsPage,
  JobPortalsPage,
  JobPortalEditPage,
  JobsPage,
  JobDetailPage,
  EmailTemplatesPage,
  SmsTemplatesPage,
  EmailSignaturePage,
  SenderEmailsPage,
  FeedbackTemplatesPage,
  FeedbackBuilderPage,
  AiInterviewTemplatesPage,
  AiInterviewTemplateEditorPage,
  InterviewGuidesPage,
  InterviewGuideEditorPage,
  OnboardingPage,
  OnboardingCasePage,
  OnboardingSettingsPage,
  FeedbackSubmissionPage,
  AiScoringPage,
  PipelineSettingsPage,
  ArchiveReasonsSettingsPage,
  CandidateEngagementPage,
  JobPostingOptionsSettingsPage,
  DepartmentsSettingsPage,
  JobTitlesSettingsPage,
  IndeedIntegrationPage,
  HrSystemPage,
  GreythrRedirect,
  GreythrReviewPage,
  LinkedInIntegrationPage,
  ConnectorsPage,
  HelpPlusSupportPage,
  CandidateContactInfoPage,
  ResumeDigestPage,
  ResumeTemplatesPage,
  AnonymizationConfigsPage,
  ReportsPage,
  BulkUploadsPage,
  OffersListPage,
  OfferBuilderPage,
  OfferTemplateListPage,
  OfferTemplateBuilderPage,
  ApprovalWorkflowPage,
  ApprovalDashboardPage,
  OfferAnalyticsDashboard,
  CandidateInboxPage,
} from "./lazyComponents";

export interface CreateRoutesConfig {
  setSessionDefault: (value: any) => void;
}

export function createAppRoutes(config: CreateRoutesConfig): IRouteDetails[] {
  const { setSessionDefault } = config;

  return [
    {
      name: "Dashboard",
      path: ROUTES.HOME,
      component: HomeRoute,
      isRealTimeDataOn: false,
    },
    {
      name: "Referral Hub",
      path: ROUTES.REFERRAL_HUB + ROUTES.QUERYPARAMS,
      component: ReferralHubOverview,
      isRealTimeDataOn: false,
    },
    {
      name: "Open Roles",
      path: ROUTES.REFERRAL_OPEN_ROLES + ROUTES.QUERYPARAMS,
      component: OpenRolesPage,
      isRealTimeDataOn: false,
    },
    {
      name: "My Referrals",
      path: ROUTES.MY_REFERRALS + ROUTES.QUERYPARAMS,
      component: MyReferralsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "Rewards",
      path: ROUTES.REFERRAL_REWARDS + ROUTES.QUERYPARAMS,
      component: RewardsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "Apps",
      path: ROUTES.APPS + ROUTES.QUERYPARAMS,
      component: Apps,
      isRealTimeDataOn: false,
    },
    {
      name: "Platform",
      path: ROUTES.PLATFORM + ROUTES.QUERYPARAMS,
      component: Platform,
      isRealTimeDataOn: false,
      extraProps: {
        setSessionDefault: setSessionDefault,
      },
    },
    {
      // Skypoint staff only: the sidebar draws it, and the guard admits it, for platform admins.
      name: "InstanceProducts",
      path: ROUTES.INSTANCE_PRODUCTS + ROUTES.QUERYPARAMS,
      component: InstanceProductsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "GeneralSettings",
      path: ROUTES.GENERAL_SETTINGS + ROUTES.QUERYPARAMS,
      component: GeneralSettingsPage,
      isRealTimeDataOn: false,
    },
    // D5 (2026-10-08 shell spec): the tiled Settings page. `/settings/<page>` is every settings
    // page's own route; a static segment outranks this one's optional `:instanceid`, so they keep
    // matching exactly as before.
    {
      name: "SettingsHub",
      path: ROUTES.SETTINGS_HUB + ROUTES.QUERYPARAMS,
      component: SettingsHubPage,
      isRealTimeDataOn: false,
    },
    {
      name: "Locations",
      path: ROUTES.LOCATIONS + ROUTES.QUERYPARAMS,
      component: LocationsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "Account",
      isRealTimeDataOn: false,
      path: ROUTES.ACCOUNT + ROUTES.QUERYPARAMS,
      component: Account,
    },
    {
      name: "SurveyCreate",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEY_CREATE + ROUTES.QUERYPARAMS,
      component: SurveyBuilderPage,
    },
    {
      name: "SurveyTemplates",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEY_TEMPLATES + ROUTES.QUERYPARAMS,
      component: SurveyTemplatesPage,
    },
    {
      name: "SurveyEdit",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEY_EDIT + ROUTES.QUERYPARAMS,
      component: SurveyBuilderPage,
    },
    {
      name: "SurveyPreview",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEY_PREVIEW + ROUTES.QUERYPARAMS,
      component: SurveyPreviewPage,
    },
    {
      name: "SurveyDetail",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEY_DETAIL + ROUTES.QUERYPARAMS,
      component: SurveyDetailPage,
    },
    {
      name: "Surveys",
      isRealTimeDataOn: false,
      path: ROUTES.SURVEYS + ROUTES.QUERYPARAMS,
      component: SurveysPage,
    },
    {
      name: "PublicSurvey",
      isRealTimeDataOn: false,
      path: ROUTES.PUBLIC_SURVEY,
      component: PublicSurveyPage,
    },
    {
      path: ROUTES.ERRORPAGE + ROUTES.QUERYPARAMS,
      name: "ErrorPage",
      component: ErrorPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.TENANTSETIINGS + ROUTES.QUERYPARAMS,
      name: "TenantsSettings",
      component: TenantsSettings,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceHome",
      path: ROUTES.PERFORMANCE + ROUTES.QUERYPARAMS,
      component: PerformanceHome,
      isRealTimeDataOn: false,
    },
    {
      // skyPerformance IT's landing (spec 2026-10-26 §4.6): lazy, guarded like every product page.
      name: "PerformanceItHome",
      path: ROUTES.PERFORMANCE_IT + ROUTES.QUERYPARAMS,
      component: PerformanceItHome,
      isRealTimeDataOn: false,
    },
    // E9: the tabbed Settings page. The tab route before the bare one, as JOB_DETAIL before JOBS:
    // `/performance/settings/<tab>` ties with `/performance/settings/:instanceid?` and the
    // first-defined route wins. The page sends the Phase 2 roles-and-wording path, and any
    // unknown tab, to Roles and wording. D5 (2026-10-08 shell spec): the bare path is the tiled
    // Settings page, no longer the Roles and wording tab.
    {
      name: "PerformanceSettingsTab",
      path: ROUTES.PERFORMANCE_SETTINGS_TAB + ROUTES.QUERYPARAMS,
      component: PerformanceSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceSettings",
      path: ROUTES.PERFORMANCE_SETTINGS + ROUTES.QUERYPARAMS,
      component: PerformanceSettingsHub,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceCoaching",
      path: ROUTES.PERFORMANCE_COACHING + ROUTES.QUERYPARAMS,
      component: CoachingLogPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceCoachingRecord",
      path: ROUTES.PERFORMANCE_COACHING_RECORD + ROUTES.QUERYPARAMS,
      component: CoachingRecordPage,
      isRealTimeDataOn: false,
    },
    // A person's file before My file, as JOB_DETAIL before JOBS: `/performance/file/<key>` ties
    // with `/performance/file/:instanceid?` and the first-defined route wins the tie.
    {
      name: "PerformanceFilePerson",
      path: ROUTES.PERFORMANCE_FILE_PERSON + ROUTES.QUERYPARAMS,
      component: PersonFilePage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceFile",
      path: ROUTES.PERFORMANCE_FILE + ROUTES.QUERYPARAMS,
      component: PersonFilePage,
      isRealTimeDataOn: false,
    },
    // E6: the tab route before the bare one, as the file's (B-18): `/performance/todos/<tab>` ties
    // with `/performance/todos/:instanceid?` and the first-defined route wins the tie.
    {
      name: "PerformanceTodosTab",
      path: ROUTES.PERFORMANCE_TODOS_TAB + ROUTES.QUERYPARAMS,
      component: TodosPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceTodos",
      path: ROUTES.PERFORMANCE_TODOS + ROUTES.QUERYPARAMS,
      component: TodosPage,
      isRealTimeDataOn: false,
    },
    // E3: the tab route, then one plan, then the bare list: `/performance/plans/t/<tab>` and
    // `/performance/plans/<id>` both tie with `/performance/plans/:instanceid?`, and the
    // first-defined route wins the tie (as the to-dos' and the file's).
    {
      name: "PerformancePlansTab",
      path: ROUTES.PERFORMANCE_PLANS_TAB + ROUTES.QUERYPARAMS,
      component: PlansPage,
      isRealTimeDataOn: false,
    },
    // E3.b R-A: the subject's own plan, before one plan: `/performance/plans/my` ties with
    // `/performance/plans/:planId` and the first-defined route wins the tie.
    {
      name: "PerformanceMyPlan",
      path: ROUTES.PERFORMANCE_PLANS_MY + ROUTES.QUERYPARAMS,
      component: MyPlanPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformancePlan",
      path: ROUTES.PERFORMANCE_PLAN + ROUTES.QUERYPARAMS,
      component: PlanPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformancePlans",
      path: ROUTES.PERFORMANCE_PLANS + ROUTES.QUERYPARAMS,
      component: PlansPage,
      isRealTimeDataOn: false,
    },
    // E4: a tab of the list, the subject's own evaluation (under Home), one evaluation, then the
    // bare list; the first-defined route wins a tie, as the plans' routes do.
    {
      name: "PerformanceEvaluationsTab",
      path: ROUTES.PERFORMANCE_EVALUATIONS_TAB + ROUTES.QUERYPARAMS,
      component: EvaluationsPage,
      isRealTimeDataOn: false,
    },
    // E4.b: the employee's self-evaluation, before their evaluation (the longer path first).
    {
      name: "PerformanceMySelfEvaluation",
      path: ROUTES.PERFORMANCE_EVALUATION_MY_SELF + ROUTES.QUERYPARAMS,
      component: SelfEvaluationPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceMyEvaluation",
      path: ROUTES.PERFORMANCE_EVALUATION_MY + ROUTES.QUERYPARAMS,
      component: EvaluationPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceEvaluation",
      path: ROUTES.PERFORMANCE_EVALUATION + ROUTES.QUERYPARAMS,
      component: EvaluationPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceEvaluations",
      path: ROUTES.PERFORMANCE_EVALUATIONS + ROUTES.QUERYPARAMS,
      component: EvaluationsPage,
      isRealTimeDataOn: false,
    },
    // E5: a tab of the list, one visit, then the bare list (the first-defined route wins a tie).
    {
      name: "PerformanceVisitsTab",
      path: ROUTES.PERFORMANCE_VISITS_TAB + ROUTES.QUERYPARAMS,
      component: VisitsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceVisit",
      path: ROUTES.PERFORMANCE_VISIT + ROUTES.QUERYPARAMS,
      component: VisitPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceVisits",
      path: ROUTES.PERFORMANCE_VISITS + ROUTES.QUERYPARAMS,
      component: VisitsPage,
      isRealTimeDataOn: false,
    },
    // E8: Records (a tab, then the bare list) and the employee's My documents (a manager's and
    // HR's documents are Records' Documents tab).
    {
      name: "PerformanceRecordsTab",
      path: ROUTES.PERFORMANCE_RECORDS_TAB + ROUTES.QUERYPARAMS,
      component: RecordsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceRecords",
      path: ROUTES.PERFORMANCE_RECORDS + ROUTES.QUERYPARAMS,
      component: RecordsPage,
      isRealTimeDataOn: false,
    },
    {
      name: "PerformanceDocuments",
      path: ROUTES.PERFORMANCE_DOCUMENTS + ROUTES.QUERYPARAMS,
      component: DocumentsPage,
      isRealTimeDataOn: false,
    },
    // E7: the organisation chart; the selected person is `?person=<key>`.
    {
      name: "PerformanceOrg",
      path: ROUTES.PERFORMANCE_ORG + ROUTES.QUERYPARAMS,
      component: OrgChartPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CREATETENANT + ROUTES.QUERYPARAMS,
      name: "CreateTenant",
      component: CreateTenant,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.LOGS + ROUTES.QUERYPARAMS,
      name: "Logs",
      component: LogsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ACCESS_MANAGEMENT + ROUTES.QUERYPARAMS,
      name: "AccessManagement",
      component: UsersPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ROLE_MANAGEMENT + ROUTES.QUERYPARAMS,
      name: "RoleManagement",
      component: RoleManagementPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CLIENTS_MANAGEMENT + ROUTES.QUERYPARAMS,
      name: "ClientsManagement",
      component: ClientsManagementPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.APPLICANTS + "/:applicantId?" + ROUTES.QUERYPARAMS,
      name: "Applicants",
      component: LeadsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CANDIDATE_INBOX + ROUTES.QUERYPARAMS,
      name: "CandidateInbox",
      component: CandidateInboxPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.TALENT_REDISCOVERY + ROUTES.QUERYPARAMS,
      name: "TalentRediscovery",
      component: RediscoveryPage,
      isRealTimeDataOn: false,
    },
    // One entry for the list and for one candidate's folder, as Applicants has:
    // two entries would match "/document-centre/<id>" against ":instanceid?" on
    // the list's route and render the list.
    {
      path: ROUTES.DOCUMENT_CENTRE + "/:applicationId?" + ROUTES.QUERYPARAMS,
      name: "DocumentCentre",
      component: DocumentCentrePage,
      isRealTimeDataOn: false,
    },
    // The case before the list, as JOB_DETAIL is before JOBS.
    {
      path: ROUTES.ONBOARDING_CASE + ROUTES.QUERYPARAMS,
      name: "OnboardingCase",
      component: OnboardingCasePage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFBOARDING + ROUTES.QUERYPARAMS,
      name: "Offboarding",
      component: OnboardingPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ONBOARDING_APPROVALS + ROUTES.QUERYPARAMS,
      name: "OnboardingApprovals",
      component: OnboardingPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ONBOARDING + ROUTES.QUERYPARAMS,
      name: "Onboarding",
      component: OnboardingPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ONBOARDING_SETTINGS + ROUTES.QUERYPARAMS,
      name: "OnboardingSettings",
      component: OnboardingSettingsPage,
      isRealTimeDataOn: false,
    },
    // The staff pages' old paths (they moved under /hris). More specific ones first.
    {
      path: ROUTES.LEGACY_ONBOARDING + "/cases/:id" + ROUTES.QUERYPARAMS,
      name: "LegacyOnboardingCase",
      component: LegacyOnboardingRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.LEGACY_ONBOARDING + "/offboarding" + ROUTES.QUERYPARAMS,
      name: "LegacyOffboarding",
      component: LegacyOnboardingRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.LEGACY_ONBOARDING_SETTINGS + ROUTES.QUERYPARAMS,
      name: "LegacyOnboardingSettings",
      component: LegacyOnboardingRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.LEGACY_ONBOARDING + ROUTES.QUERYPARAMS,
      name: "LegacyOnboarding",
      component: LegacyOnboardingRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.BULK_UPLOADS + ROUTES.QUERYPARAMS,
      name: "BulkUploads",
      component: BulkUploadsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SCHEDULED_TOURS + ROUTES.QUERYPARAMS,
      name: "ScheduledTours",
      component: ScheduledToursPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CRM_INTEGRATIONS + ROUTES.QUERYPARAMS,
      name: "CrmIntegrations",
      component: CrmIntegrationsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_PORTALS + ROUTES.QUERYPARAMS,
      name: "JobPortals",
      component: JobPortalsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_PORTAL_EDIT + ROUTES.QUERYPARAMS,
      name: "JobPortalEdit",
      component: JobPortalEditPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_CREATE + ROUTES.QUERYPARAMS,
      name: "JobCreate",
      component: JobDetailPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_DETAIL + ROUTES.QUERYPARAMS,
      name: "JobDetail",
      component: JobDetailPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOBS + ROUTES.QUERYPARAMS,
      name: "Jobs",
      component: JobsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ONBOARD_PACKETS + ROUTES.QUERYPARAMS,
      name: "OnboardPackets",
      component: OnboardDashboard,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ONBOARD_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "OnboardTemplates",
      component: OfferTemplatesPage,
      isRealTimeDataOn: false,
    },
    // ── skyAgent ──────────────────────────────────────────────────────
    // The two section roots are declared last, after their children. That is
    // for reading, not for matching: react-router 7 ranks branches by
    // specificity (rankRouteBranches/computeScore), so "/skyagent/approvals"
    // beats "/skyagent/:instanceid?" wherever either one sits in this array.
    {
      path: ROUTES.SKYAGENT_AI_EMPLOYEES + ROUTES.QUERYPARAMS,
      name: "SkyAgentAIEmployees",
      component: SkyAgentAIEmployeesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT_APPROVALS + ROUTES.QUERYPARAMS,
      name: "SkyAgentApprovals",
      component: SkyAgentApprovalsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT_ARTIFACTS + ROUTES.QUERYPARAMS,
      name: "SkyAgentArtifacts",
      component: SkyAgentArtifactsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT_CONFIGURATION + ROUTES.QUERYPARAMS,
      name: "SkyAgentConfiguration",
      component: SkyAgentConfigurationPage,
      isRealTimeDataOn: false,
    },
    // ── skyBrain ──────────────────────────────────────────────────────
    {
      path: ROUTES.SKYBRAIN_MEMORY + ROUTES.QUERYPARAMS,
      name: "SkyBrainMemory",
      component: SkyBrainMemoryPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYBRAIN_SKILLS + ROUTES.QUERYPARAMS,
      name: "SkyBrainSkills",
      component: SkyBrainSkillsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYBRAIN_QUICK_PROMPTS + ROUTES.QUERYPARAMS,
      name: "SkyBrainQuickPrompts",
      component: SkyBrainQuickPromptsPage,
      isRealTimeDataOn: false,
    },
    // ── retired URLs ──────────────────────────────────────────────────
    // Bookmarks and already-sent links. Each redirects to the surface that
    // replaced it, carrying the instance id and query string across.
    {
      path: ROUTES.SKYAGENT_ADMIN + ROUTES.QUERYPARAMS,
      name: "SkyAgentAdminRedirect",
      component: SkyAgentAdminRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT_MEMORY + ROUTES.QUERYPARAMS,
      name: "SkyAgentMemoryRedirect",
      component: SkyAgentMemoryRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT_CAPABILITIES + ROUTES.QUERYPARAMS,
      name: "SkyAgentCapabilitiesRedirect",
      component: SkyAgentCapabilitiesRedirect,
      isRealTimeDataOn: false,
    },
    {
      // Without this, "/skybrain/session-history" would match the Overview's
      // "/skybrain/:instanceid?" and read "session-history" as an instance id.
      path: ROUTES.SKYBRAIN_SESSION_HISTORY + ROUTES.QUERYPARAMS,
      name: "SkyBrainSessionHistoryRedirect",
      component: SkyBrainSessionHistoryRedirect,
      isRealTimeDataOn: false,
    },
    // ── section roots ─────────────────────────────────────────────────
    {
      path: ROUTES.SKYBRAIN + ROUTES.QUERYPARAMS,
      name: "SkyBrainOverview",
      component: SkyBrainOverviewPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SKYAGENT + ROUTES.QUERYPARAMS,
      name: "SkyAgent",
      component: SkyAgentPage,
      isRealTimeDataOn: false,
    },
    // The tiled Templates page. `/templates/<page>` is each template page's own route; a static
    // segment outranks this one's optional `:instanceid`, so they keep matching exactly as before
    // (as `/settings` does for the settings pages).
    {
      path: ROUTES.TEMPLATES_HUB + ROUTES.QUERYPARAMS,
      name: "TemplatesHub",
      component: TemplatesHubPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.EMAIL_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "EmailTemplates",
      component: EmailTemplatesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SMS_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "SmsTemplates",
      component: SmsTemplatesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.EMAIL_SIGNATURE + ROUTES.QUERYPARAMS,
      name: "EmailSignature",
      component: EmailSignaturePage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.SENDER_EMAILS + ROUTES.QUERYPARAMS,
      name: "SenderEmails",
      component: SenderEmailsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.FEEDBACK_TEMPLATE_CREATE + ROUTES.QUERYPARAMS,
      name: "FeedbackTemplateCreate",
      component: FeedbackBuilderPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.FEEDBACK_TEMPLATE_EDIT + ROUTES.QUERYPARAMS,
      name: "FeedbackTemplateEdit",
      component: FeedbackBuilderPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.FEEDBACK_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "FeedbackTemplates",
      component: FeedbackTemplatesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.AI_INTERVIEW_TEMPLATE_CREATE + ROUTES.QUERYPARAMS,
      name: "AiInterviewTemplateCreate",
      component: AiInterviewTemplateEditorPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.AI_INTERVIEW_TEMPLATE_EDIT + ROUTES.QUERYPARAMS,
      name: "AiInterviewTemplateEdit",
      component: AiInterviewTemplateEditorPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.AI_INTERVIEW_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "AiInterviewTemplates",
      component: AiInterviewTemplatesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.INTERVIEW_GUIDE_CREATE + ROUTES.QUERYPARAMS,
      name: "InterviewGuideCreate",
      component: InterviewGuideEditorPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.INTERVIEW_GUIDE_EDIT + ROUTES.QUERYPARAMS,
      name: "InterviewGuideEdit",
      component: InterviewGuideEditorPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.INTERVIEW_GUIDES + ROUTES.QUERYPARAMS,
      name: "InterviewGuides",
      component: InterviewGuidesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.FEEDBACK_SUBMISSION + ROUTES.QUERYPARAMS,
      name: "FeedbackSubmission",
      component: FeedbackSubmissionPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.AI_SCORING + ROUTES.QUERYPARAMS,
      name: "AiScoring",
      component: AiScoringPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.PIPELINE_SETTINGS + ROUTES.QUERYPARAMS,
      name: "PipelineSettings",
      component: PipelineSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ARCHIVE_REASONS_SETTINGS + ROUTES.QUERYPARAMS,
      name: "ArchiveReasonsSettings",
      component: ArchiveReasonsSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CANDIDATE_ENGAGEMENT_RULES + ROUTES.QUERYPARAMS,
      name: "CandidateEngagementRules",
      component: CandidateEngagementPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CANDIDATE_ENGAGEMENT_SETTINGS + ROUTES.QUERYPARAMS,
      name: "CandidateEngagementSettings",
      component: CandidateEngagementPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_POSTING_OPTIONS_SETTINGS + ROUTES.QUERYPARAMS,
      name: "JobPostingOptionsSettings",
      component: JobPostingOptionsSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.DEPARTMENTS_SETTINGS + ROUTES.QUERYPARAMS,
      name: "DepartmentsSettings",
      component: DepartmentsSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.JOB_TITLES_SETTINGS + ROUTES.QUERYPARAMS,
      name: "JobTitlesSettings",
      component: JobTitlesSettingsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.INDEED_INTEGRATION + ROUTES.QUERYPARAMS,
      name: "IndeedIntegration",
      component: IndeedIntegrationPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.LINKEDIN_INTEGRATION + ROUTES.QUERYPARAMS,
      name: "LinkedInIntegration",
      component: LinkedInIntegrationPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.HRIS_INTEGRATION + ROUTES.QUERYPARAMS,
      name: "HrisIntegration",
      component: HrSystemPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.GREYTHR_INTEGRATION + ROUTES.QUERYPARAMS,
      name: "GreythrIntegration",
      component: GreythrRedirect,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.GREYTHR_REVIEW + ROUTES.QUERYPARAMS,
      name: "GreythrReview",
      component: GreythrReviewPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CONNECTORS + ROUTES.QUERYPARAMS,
      name: "Connectors",
      component: ConnectorsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.HELP_PLUS_SUPPORT + ROUTES.QUERYPARAMS,
      name: "HelpPlusSupport",
      component: HelpPlusSupportPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.CANDIDATE_CONTACT_INFO + ROUTES.QUERYPARAMS,
      name: "CandidateContactInfo",
      component: CandidateContactInfoPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.RESUME_DIGEST + ROUTES.QUERYPARAMS,
      name: "ResumeDigest",
      component: ResumeDigestPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.RESUME_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "ResumeTemplates",
      component: ResumeTemplatesPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.ANONYMIZATION_CONFIGS + ROUTES.QUERYPARAMS,
      name: "AnonymizationConfigs",
      component: AnonymizationConfigsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.REPORTS + ROUTES.QUERYPARAMS,
      name: "Reports",
      component: ReportsPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_CREATE + ROUTES.QUERYPARAMS,
      name: "OfferCreate",
      component: OfferBuilderPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_DETAIL + ROUTES.QUERYPARAMS,
      name: "OfferDetail",
      component: OfferBuilderPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_TEMPLATE_EDIT + ROUTES.QUERYPARAMS,
      name: "OfferTemplateEdit",
      component: OfferTemplateBuilderPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_TEMPLATES + ROUTES.QUERYPARAMS,
      name: "OfferTemplates",
      component: OfferTemplateListPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_WORKFLOWS + ROUTES.QUERYPARAMS,
      name: "OfferWorkflows",
      component: ApprovalWorkflowPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_APPROVALS + ROUTES.QUERYPARAMS,
      name: "OfferApprovals",
      component: ApprovalDashboardPage,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFER_ANALYTICS + ROUTES.QUERYPARAMS,
      name: "OfferAnalytics",
      component: OfferAnalyticsDashboard,
      isRealTimeDataOn: false,
    },
    {
      path: ROUTES.OFFERS + ROUTES.QUERYPARAMS,
      name: "Offers",
      component: OffersListPage,
      isRealTimeDataOn: false,
    },
  ];
}
